import { Chip, ChipProps } from '@mui/material';
import { VisitCounselState } from '@/entities/visit-counsel/model/visit-counsel.types';

/** CounselState 기반: APPOINTED(시작전), STARTED(진행중), COMPLETED/completedAt(완료됨) */
export type VisitCounselStatusVariant = 'appointed' | 'started' | 'completed';

export interface VisitCounselStatusChipProps {
  variant: VisitCounselStatusVariant;
  size?: ChipProps['size'];
  sx?: ChipProps['sx'];
}

const VARIANT_CONFIG: Record<
  VisitCounselStatusVariant,
  { label: string; color: ChipProps['color']; filled: boolean }
> = {
  appointed: {
    label: '시작전',
    color: 'default',
    filled: false,
  },
  started: {
    label: '진행중',
    color: 'primary',
    filled: true,
  },
  completed: {
    label: '완료됨',
    color: 'success',
    filled: true,
  },
};

/** VisitCounselView → VisitCounselStatusVariant (completedAt·state·startedAt 순으로 판별) */
export function counselStateToVariant(
  stateOrCounsel?:
    | VisitCounselState
    | null
    | {
        state?: VisitCounselState | null;
        startedAt?: string | null;
        completedAt?: string | null;
      }
    | Record<string, unknown>
): VisitCounselStatusVariant {
  if (stateOrCounsel == null) return 'appointed';
  if (typeof stateOrCounsel === 'string') {
    if (stateOrCounsel === VisitCounselState.COMPLETED) return 'completed';
    return stateOrCounsel === VisitCounselState.STARTED ? 'started' : 'appointed';
  }
  const c = stateOrCounsel as Record<string, unknown>;
  const completedAt = (c.completedAt ?? c.completed_at) as string | null | undefined;
  if (completedAt) return 'completed';
  const state = (c.state ?? c.visitCounselState ?? c.visit_counsel_state) as
    | VisitCounselState
    | undefined;
  if (state === VisitCounselState.COMPLETED) return 'completed';
  if (state === VisitCounselState.STARTED) return 'started';
  if (c.startedAt) return 'started'; // startedAt 존재 시 진행중 (백엔드가 state 생략 시 fallback)
  return 'appointed';
}

export function VisitCounselStatusChip({
  variant,
  size = 'small',
  sx,
}: VisitCounselStatusChipProps) {
  const config = VARIANT_CONFIG[variant];
  return (
    <Chip
      label={config.label}
      color={config.color}
      variant={config.filled ? 'filled' : 'outlined'}
      size={size}
      sx={{
        height: 20,
        fontSize: '0.7rem',
        fontWeight: 600,
        ...sx,
      }}
    />
  );
}
